import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";

const EXTRA_LEDGERS = [
    { key: "factory",  titleAr: "دفتر المصانع",  titleEn: "Factories" },
    { key: "client",   titleAr: "دفتر العملاء",   titleEn: "Customers" },
    { key: "vouchers", titleAr: "سندات الصرف",    titleEn: "Payment Vouchers" },
];

export default function ManagerEmployeePerms() {
    const navigate = useNavigate();
    const { company, refresh } = useAuth();
    const { lang } = useLang();
    const [decision, setDecision] = useState(null); // null | true | false
    const [picked, setPicked] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [dailyOn, setDailyOn] = useState(null); // null | true | false
    const [dailyVariant, setDailyVariant] = useState(null);

    if (company?.account_type !== "manager") {
        navigate("/", { replace: true });
        return null;
    }

    const toggle = (k) =>
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });

    const submit = async () => {
        const enabled = decision === true;
        const ledgers = enabled ? EXTRA_LEDGERS.map((o) => o.key).filter((k) => picked.has(k)) : [];
        if (enabled && ledgers.length === 0) {
            toast.error(lang === "ar" ? "اختر دفتراً واحداً على الأقل" : "Pick at least one ledger");
            return;
        }
        if (dailyOn === null || (dailyOn && !dailyVariant)) {
            toast.error(lang === "ar" ? "حدد إعداد دفتر الحركة للموظفين" : "Choose the daily-ledger setting for employees");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/manager/employee-ledger-perms", { enabled, ledgers, daily_enabled: dailyOn, daily_variant: dailyOn ? dailyVariant : null });
            await refresh();
            toast.success(lang === "ar" ? "تم حفظ صلاحيات الموظفين" : "Employee permissions saved");
            navigate("/manager/employee-access");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen app-bg" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
                <div className="mb-8 text-center">
                    <div className="inline-block rounded-full bg-amber-100 px-4 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-900">
                        {lang === "ar" ? "خطوة 2 من 3 — صلاحيات الموظفين" : "Step 2 of 3 — Employee permissions"}
                    </div>
                    <h1 className="mt-4 font-heading text-3xl font-bold text-neutral-900 sm:text-4xl">
                        {lang === "ar"
                            ? "هل تريد السماح للموظفين بالوصول إلى دفاتر إضافية؟"
                            : "Allow employees access to additional ledgers?"}
                    </h1>
                    <p className="mx-auto mt-3 max-w-xl text-sm text-neutral-600">
                        {lang === "ar"
                            ? "حدد أولاً هل يستخدم موظفوك دفتر الحركة ونوعه، ثم الدفاتر الإضافية. يمكنك تغيير إعداد دفتر الحركة لكل موظف لاحقاً من صفحة الموظفين."
                            : "First decide whether your employees use the daily ledger and its type, then any extra ledgers. You can change the daily setting per employee later."}
                    </p>
                </div>

                <div className="mx-auto mb-8 max-w-lg rounded-2xl border border-neutral-200 bg-white p-5" data-testid="mep-daily-section">
                    <h3 className="font-heading text-base font-bold text-neutral-800">{lang === "ar" ? "دفتر الحركة للموظفين" : "Daily ledger for employees"}</h3>
                    <div className="mt-3 flex gap-2">
                        {[[true, lang === "ar" ? "مفعّل" : "Enabled", "on"], [false, lang === "ar" ? "غير مفعّل" : "Disabled", "off"]].map(([v, l, k]) => (
                            <button key={k} type="button" onClick={() => setDailyOn(v)} data-testid={`mep-daily-${k}`}
                                className={`flex-1 rounded-xl border-2 py-2.5 text-sm font-bold ${dailyOn === v ? "border-amber-700 bg-amber-50 text-amber-900" : "border-neutral-200 text-neutral-600"}`}>{l}</button>
                        ))}
                    </div>
                    {dailyOn ? (
                        <div className="mt-3 flex gap-2">
                            {[["standard", lang === "ar" ? "عادي" : "Standard"], ["advanced", lang === "ar" ? "محترف" : "Advanced"]].map(([v, l]) => (
                                <button key={v} type="button" onClick={() => setDailyVariant(v)} data-testid={`mep-daily-variant-${v}`}
                                    className={`flex-1 rounded-xl border-2 py-2.5 text-sm font-bold ${dailyVariant === v ? "border-amber-700 bg-amber-50 text-amber-900" : "border-neutral-200 text-neutral-600"}`}>{l}</button>
                            ))}
                        </div>
                    ) : null}
                </div>

                <h3 className="mb-3 text-center font-heading text-base font-bold text-neutral-800">{lang === "ar" ? "دفاتر إضافية للموظفين؟" : "Extra ledgers for employees?"}</h3>

                <div className="mx-auto grid max-w-lg gap-3 sm:grid-cols-2">
                    <button
                        type="button"
                        onClick={() => setDecision(true)}
                        data-testid="mep-yes-btn"
                        className={`rounded-2xl border-2 p-5 transition ${
                            decision === true
                                ? "border-emerald-600 bg-emerald-50 shadow-md"
                                : "border-neutral-200 bg-white hover:border-emerald-300"
                        }`}
                    >
                        <CheckCircle2 className={`mx-auto h-8 w-8 ${decision === true ? "text-emerald-600" : "text-neutral-300"}`} />
                        <div className="mt-2 font-heading text-lg font-bold">{lang === "ar" ? "نعم" : "Yes"}</div>
                        <div className="text-xs text-neutral-500">{lang === "ar" ? "اختر الدفاتر بنفسك" : "Pick the ledgers"}</div>
                    </button>
                    <button
                        type="button"
                        onClick={() => setDecision(false)}
                        data-testid="mep-no-btn"
                        className={`rounded-2xl border-2 p-5 transition ${
                            decision === false
                                ? "border-rose-500 bg-rose-50 shadow-md"
                                : "border-neutral-200 bg-white hover:border-rose-300"
                        }`}
                    >
                        <X className={`mx-auto h-8 w-8 ${decision === false ? "text-rose-500" : "text-neutral-300"}`} />
                        <div className="mt-2 font-heading text-lg font-bold">{lang === "ar" ? "لا" : "No"}</div>
                        <div className="text-xs text-neutral-500">{lang === "ar" ? "بدون دفاتر إضافية" : "No extra ledgers"}</div>
                    </button>
                </div>

                {decision === true ? (
                    <div className="mx-auto mt-8 max-w-2xl">
                        <h3 className="mb-3 font-heading text-base font-bold text-neutral-800">
                            {lang === "ar" ? "اختر الدفاتر المسموح بها للموظفين:" : "Pick ledgers allowed for employees:"}
                        </h3>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {EXTRA_LEDGERS.map((opt) => {
                                const selected = picked.has(opt.key);
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => toggle(opt.key)}
                                        data-testid={`mep-extra-${opt.key}`}
                                        className={`flex items-center justify-between rounded-xl border-2 px-4 py-3 transition ${
                                            selected
                                                ? "border-amber-700 bg-amber-50"
                                                : "border-neutral-200 bg-white hover:border-amber-300"
                                        }`}
                                    >
                                        <span className="font-semibold">{lang === "ar" ? opt.titleAr : opt.titleEn}</span>
                                        {selected ? (
                                            <CheckCircle2 className="h-5 w-5 text-amber-700" />
                                        ) : (
                                            <div className="h-5 w-5 rounded-full border-2 border-neutral-300" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : null}

                <div className="mt-8 flex justify-center">
                    <Button
                        type="button"
                        size="lg"
                        onClick={submit}
                        disabled={decision === null || saving}
                        data-testid="mep-submit-btn"
                        className="h-11 gap-2 bg-amber-700 px-8 text-white hover:bg-amber-800"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {lang === "ar" ? "حفظ ومتابعة" : "Save & continue"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
